import { fetchData } from "@repo/shared-utils/fetch-utils";
import { decrypt, encrypt, generateSalt } from "@repo/shared-utils/crypto";
import { getCookie } from "@repo/shared-utils/common-utils";
import { storeEncryptedResInLocalStorage } from "./fpr-utils";
import { COMMON_SSO_ACCESS_TOKEN } from "@repo/shared-utils/sso.constants";

export interface IResponse<T> {
  data: T;
  statusCode: number;
  status: "success" | "error";
  message: string;
}

// Define types for request and response
interface APIRequestParams<TRequest> {
  method: "GET" | "POST";
  url: string;
  data?: TRequest;
  storeResponseInLocal?: boolean;
  storageKey?: string;
  signal?: AbortSignal;
  cache?: RequestCache;
  revalidate?: number;
}

/**
 * addAccessTokenToData
 * @param data
 * @returns
 */
export const addAccessTokenToData = <T extends { mobileNo?: string }>(
  data?: T,
): T => {
  try {
    if (!data) return {} as T;

    const accessToken = localStorage.getItem(COMMON_SSO_ACCESS_TOKEN);

    return {
      ...data,
      ...(accessToken ? { accessToken } : {}),
    };
  } catch {
    return data ?? ({} as T);
  }
};

export const encryptedRequest = async <TResponse, TRequest = unknown>(
  params: APIRequestParams<TRequest>,
): Promise<IResponse<TResponse>> => {
  const { method, url, data, cache, revalidate } = params;
  const modifiedData = addAccessTokenToData(
    data as { mobileNo: string | undefined } | undefined,
  );
  const csrfToken = getCookie("CSRFtoken");
  const salt: string = await generateSalt();
  const encryptedData: string | undefined = await encrypt(
    JSON.stringify(modifiedData),
    salt,
  );

  const API_Domain = process.env.NEXT_PUBLIC_WEB_SERVICES_DOMAIN;
  // Check if URL already contains protocol (http:// or https://)
  const isFullUrl = url.startsWith("http://") || url.startsWith("https://");
  const endpoint = isFullUrl
    ? url
    : API_Domain
      ? `${API_Domain}${url}`
      : url;
  const response = await fetchData({
    endpoint,
    options: {
      method,
      skipCredentials: true,
    },
    body: {
      reqData: encryptedData,
      _csrf: csrfToken,
    },
    headers: {
      reqKey: salt,
      pageURL: window.location.href,
    },
    cache,
    revalidate,
  });
  let decrpytedRes: any = null;
  if (typeof response === "string") {
    decrpytedRes = await decrypt(response, salt);
  } else {
    decrpytedRes = response;
  }
  if (decrpytedRes?.status === "success") {
    if (params.storeResponseInLocal && params.storageKey) {
      storeEncryptedResInLocalStorage({
        rK: salt,
        rD: response,
        key: params.storageKey,
      });
      return decrpytedRes;
    }

    if (decrpytedRes?.status === "error") {
      throw decrpytedRes;
    }
    return decrpytedRes;
  }

  return decrpytedRes;
};
